import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Mail, Palette, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const card = "bg-white rounded-[16px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";
const btn = "inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60";
const primary = `${btn} bg-ec-green text-white hover:bg-ec-greenDark`;
const ghost = `${btn} border border-slate-200 text-ec-green hover:bg-slate-50`;
const err = (e) => toast.error(apiErr(e.response?.data?.detail));

export function PaletteSuggest({ onApplied }) {
  const [pal, setPal] = useState(null);
  const suggest = async () => { try { setPal((await api.post("/agency/branding/palette")).data); } catch (e) { err(e); } };
  const apply = async () => { try { await api.put("/agency/branding", pal); toast.success("Cores do logótipo aplicadas"); setPal(null); onApplied?.(); } catch (e) { err(e); } };
  return (
    <div className={card} data-testid="palette-suggest">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div><h3 className="font-heading text-lg text-ec-green">Cores a partir do logótipo</h3><p className="text-sm text-slate-500">Principal, escura e destaque, tiradas do logótipo carregado.</p></div>
        <button onClick={suggest} className={ghost} data-testid="palette-suggest-btn"><Palette size={15} /> Sugerir cores</button>
      </div>
      {pal && (
        <div className="mt-4 flex flex-wrap items-center gap-4">
          {[["color", "Principal"], ["dark_color", "Escura"], ["accent_color", "Destaque"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-slate-600"><input type="color" value={pal[k]} onChange={(e) => setPal({ ...pal, [k]: e.target.value.toUpperCase() })} className="w-9 h-9 rounded border border-slate-200" data-testid={`palette-${k}`} />{l} <span className="font-mono text-xs">{pal[k]}</span></label>
          ))}
          <button onClick={apply} className={primary} data-testid="palette-apply">Usar estas cores</button>
        </div>
      )}
    </div>
  );
}

export function BrandPreviews() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/agency/branding").then(({ data }) => setB(data)); }, []);
  if (!b) return null;
  const logo = b.logo ? <img src={b.logo} alt="" className="h-7 max-w-[120px] object-contain" /> : <span className="font-semibold text-sm" style={{ color: b.dark_color }}>{b.trade_name}</span>;
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="brand-previews">
      <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="preview-portal">
        <div className="px-3 py-2.5 flex items-center gap-2 border-b-2 bg-white" style={{ borderBottomColor: b.color }}>{logo}</div>
        <div className="p-3 text-xs text-slate-600 space-y-2"><div className="font-semibold" style={{ color: b.dark_color }}>{b.portal_name}</div><div>Faltam 3 documentos</div>
          <div className="h-7 rounded-md grid place-items-center text-white font-semibold" style={{ background: b.color }}>Enviar documentos</div></div>
        <div className="px-3 pb-2 text-[10px] text-slate-400">Portal do cliente</div>
      </div>
      <div className="rounded-xl border border-slate-200 p-3 text-xs text-slate-600" data-testid="preview-email">
        {logo}<div className="h-0.5 my-2" style={{ background: b.color }} />
        <p>Olá Ana. Para preparar a venda, pode enviar os documentos aqui.</p>
        <div className="mt-2 inline-block px-3 py-1.5 rounded-md text-white font-semibold" style={{ background: b.dark_color }}>Entregar documentos</div>
        <p className="mt-2 text-[10px] text-slate-400">{b.name}{b.white_label ? "" : " · powered by ImoDesk"} · Email</p>
      </div>
      <div className="rounded-xl p-3 text-xs" style={{ background: "#E5DDD5" }} data-testid="preview-whatsapp">
        <div className="bg-[#DCF8C6] rounded-lg p-2 ml-6 shadow-sm">
          <div className="bg-white/70 rounded-md p-2 flex gap-2 items-center border-l-4" style={{ borderLeftColor: b.color }}>
            {b.logo ? <img src={b.logo} alt="" className="w-10 h-10 object-contain bg-white rounded" /> : null}
            <div className="min-w-0"><div className="font-semibold text-slate-800 truncate">{b.trade_name} · Os seus documentos</div><div className="text-slate-500 truncate">imodesk.pt</div></div>
          </div>
          <div className="mt-1.5 text-slate-700">Olá Ana, pode enviar os documentos por este link.</div>
        </div>
        <p className="mt-2 text-[10px] text-slate-500">Pré-visualização do link no WhatsApp</p>
      </div>
    </div>
  );
}

const REPLY = [["consultor", "Consultor que envia"], ["agencia", "Email geral da agência"], ["gestao", "Gestão processual"]];

export function EmailsStep() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/agency/email-settings").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  const save = async () => { try { setS((await api.put("/agency/email-settings", { sender_name: s.sender_name, reply_to: s.reply_to })).data); toast.success("Emails guardados"); } catch (e) { err(e); } };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/agency/email-test"); toast.success(`Enviado para ${data.to} · responder para ${data.reply_to || "quem envia"}`); } catch (e) { err(e); } finally { setBusy(false); }
  };
  const shown = (s.sender_name || s.default_name) + (s.white_label ? "" : " via ImoDesk");
  return (
    <div className={`${card} space-y-5`} data-testid="emails-step">
      <div><Label htmlFor="es-name">Nome do remetente</Label>
        <Input id="es-name" value={s.sender_name} placeholder={s.default_name} onChange={(e) => setS({ ...s, sender_name: e.target.value })} className="mt-1.5" data-testid="emails-sender" />
        <p className="text-xs text-slate-500 mt-1">Os clientes veem: <b data-testid="emails-from-preview">{shown}</b></p></div>
      <div><Label>«Responder para»</Label>
        <div className="mt-2 grid sm:grid-cols-3 gap-2">
          {REPLY.map(([k, l]) => (
            <button key={k} onClick={() => setS({ ...s, reply_to: k })} className={`text-sm rounded-lg border px-3 py-2.5 text-left transition-colors ${s.reply_to === k ? "border-ec-green bg-emerald-50 text-ec-green font-semibold" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`emails-reply-${k}`}>{l}</button>
          ))}
        </div>
        {s.reply_to === "agencia" && <p className="text-xs text-slate-500 mt-1">{s.general_email || "Indique o email geral no passo Identidade."}</p>}</div>
      <div><Label>Rodapé automático</Label>
        <div className="mt-1.5 rounded-lg bg-slate-50 border border-slate-100 py-3" data-testid="emails-footer" dangerouslySetInnerHTML={{ __html: s.footer_html || "<div style='padding:0 24px;font-size:12px;color:#9ca3af'>Preencha a Identidade para gerar o rodapé.</div>" }} /></div>
      <div className="flex flex-wrap gap-2">
        <button onClick={save} className={primary} data-testid="emails-save">Guardar</button>
        <button onClick={test} disabled={busy} className={ghost} data-testid="emails-test"><Mail size={15} /> {busy ? "A enviar…" : "Enviar email de teste para mim"}</button>
      </div>
    </div>
  );
}

export function EntradaStep({ setup, onSaved }) {
  const [slug, setSlug] = useState(setup?.slug || "");
  const base = (setup?.entry_url || "").replace(/\/[^/]+\/entrar$/, "");
  const save = async () => { try { onSaved((await api.put("/agency/slug", { slug })).data); toast.success("Endereço confirmado"); } catch (e) { err(e); } };
  const entry = `${base}/${slug}/entrar`;
  return (
    <div className={`${card} space-y-4`} data-testid="entrada-step">
      <div><Label htmlFor="slug">Endereço da agência</Label>
        <div className="mt-1.5 flex items-center gap-2"><span className="text-sm text-slate-500">{base.replace(/^https?:\/\//, "")}/</span>
          <Input id="slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} className="max-w-xs" data-testid="entrada-slug" /></div></div>
      <div className="text-sm text-slate-600 space-y-1">
        <div>Entrada da equipa: <b data-testid="entrada-url">{entry}</b></div>
        <div>Links dos clientes: <b>{base}/{slug}/p/AbC123x</b></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={save} className={primary} data-testid="entrada-save">Confirmar endereço</button>
        <button onClick={() => { navigator.clipboard.writeText(entry); toast.success("Link de entrada copiado"); }} className={ghost} data-testid="entrada-copy"><Copy size={15} /> Copiar link de entrada</button>
      </div>
    </div>
  );
}

export function DocumentosStep() {
  const [c, setC] = useState(null);
  const [mins, setMins] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const loadAll = () => { api.get("/agency/cmi-template").then(({ data }) => setC(data)); api.get("/agency/cmi-minutas").then(({ data }) => setMins(data)); };
  useEffect(() => { loadAll(); }, []);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Envie um PDF até 5 MB."); return; }
    setBusy(true);
    try {
      const pdf = await new Promise((ok) => { const r = new FileReader(); r.onload = () => ok(r.result); r.readAsDataURL(f); });
      const { data } = await api.post("/agency/cmi-minutas", { name: name || f.name.replace(/\.pdf$/i, ""), pdf });
      toast.success(`Minuta enviada para revisão · ${data.mapped} de ${data.total} linhas ligadas`); setName(""); loadAll();
    } catch (er) { err(er); } finally { setBusy(false); }
  };
  if (!c) return null;
  const pick = async (t) => { try { setC((await api.put("/agency/cmi-template", { template: t })).data); toast.success("Minuta do CMI escolhida"); } catch (e) { err(e); } };
  return (
    <div className={`${card} space-y-4`} data-testid="documentos-step">
      <div><h3 className="font-heading text-lg text-ec-green">Contrato de mediação (CMI)</h3><p className="text-sm text-slate-500">Escolha a minuta usada pela agência.</p></div>
      <div className="grid sm:grid-cols-2 gap-2">
        {c.options.map((o) => (
          <button key={o.key} onClick={() => pick(o.key)} className={`text-sm rounded-lg border px-3 py-3 text-left transition-colors ${c.current === o.key ? "border-ec-green bg-emerald-50 text-ec-green font-semibold" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`cmi-pick-${o.key}`}>{o.label}</button>
        ))}
      </div>
      <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="cmi-upload">
        <div className="text-sm font-semibold text-slate-700">Carregar uma minuta nova</div>
        <p className="text-xs text-slate-500">PDF com texto (não digitalizado) e linhas «____» para preencher. A IA liga cada linha a um dado do processo; a equipa ImoDesk revê e ativa.</p>
        <div className="flex flex-wrap gap-2 items-center">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome da minuta (ex.: CMI 2026)" className="max-w-xs" data-testid="cmi-upload-name" />
          <label className={`${ghost} cursor-pointer`}><Upload size={15} /> {busy ? "A ler a minuta…" : "Escolher PDF"}<input type="file" accept="application/pdf" className="hidden" disabled={busy} onChange={upload} data-testid="cmi-upload-file" /></label>
        </div>
        {mins.map((m) => (
          <div key={m.id} className="text-sm flex flex-wrap gap-2 items-center" data-testid={`cmi-minuta-${m.id}`}>
            <b>{m.name}</b><span className={`text-xs px-2 py-0.5 rounded-full ${m.status === "ativa" ? "bg-emerald-50 text-emerald-700" : m.status === "recusada" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>{m.status_label}</span>
            <span className="text-xs text-slate-500">{m.mapped} de {m.total} linhas ligadas{m.reason ? ` · ${m.reason}` : ""}</span>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-dashed border-slate-200 px-3 py-3 text-sm text-slate-400" data-testid="cpcv-soon">CPCV · em breve</div>
    </div>
  );
}

export function EquipaImport() {
  const [text, setText] = useState("");
  const [rows, setRows] = useState(null);
  const preview = async (t) => { try { setRows((await api.post("/agency/invites/preview", { text: t ?? text })).data.rows); } catch (e) { err(e); } };
  const file = async (e) => { const f = e.target.files?.[0]; if (!f) return; const t = await f.text(); setText(t); preview(t); };
  const send = async () => {
    try { const { data } = await api.post("/agency/invites/batch", { rows: rows.filter((r) => r.ok) }); setRows(data.rows); toast.success(`${data.sent} convite(s) enviados`); } catch (e) { err(e); }
  };
  const ok = (rows || []).filter((r) => r.ok).length;
  return (
    <div className={`${card} space-y-3`} data-testid="equipa-import">
      <div><h3 className="font-heading text-lg text-ec-green">Convidar a equipa</h3><p className="text-sm text-slate-500">Uma pessoa por linha: nome; email; telemóvel; papel (consultor, gestor ou direção). O telemóvel liga o assistente de WhatsApp.</p></div>
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={"Ana Martins; ana@agencia.pt; 912345678; consultor"} data-testid="equipa-csv" />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => preview()} className={ghost} data-testid="equipa-preview">Ver lista</button>
        <label className={`${ghost} cursor-pointer`}><Upload size={15} /> Importar CSV<input type="file" accept=".csv,text/csv" className="hidden" onChange={file} data-testid="equipa-csv-file" /></label>
      </div>
      {rows && (
        <div className="space-y-1.5" data-testid="equipa-rows">
          {rows.map((r, i) => (
            <div key={i} className={`text-sm rounded-lg px-3 py-2 ${r.ok ? "bg-slate-50" : "bg-red-50 text-red-700"}`} data-testid={`equipa-row-${i}`}>
              <b>{r.name || "—"}</b> · {r.email} {r.phone ? `· ${r.phone}` : ""} · {r.role || "?"}{r.error ? ` · ${r.error}` : ""}{r.emailed === false ? " · convite criado, email não enviado" : ""}
            </div>
          ))}
          <button onClick={send} disabled={!ok} className={primary} data-testid="equipa-send">Enviar {ok} convite(s)</button>
        </div>
      )}
    </div>
  );
}
